import { useCallback, useEffect, useRef, useState } from 'react';
import { runStreamService } from '../../../shared/workers/RunStreamService';
import BatchService from '../../../shared/middleware/services/BatchService';
import { useBatchRunActions, useBatchRunStore, selectGroupingOutcome, MEMBER_STATUS } from '../stores/batchRunStore';
import { resolveRunOutcome } from '../../../shared/utils/permutationStatus';
import logger from '../../../shared/services/logging/logger';

/**
 * Owns a batch's live lifecycle: submit, stream, expand/collapse, cancel,
 * rehydrate, and the degraded-stream fallback.
 *
 * One socket, many subscriptions. Expanding a member subscribes to its runId
 * for the full payload; collapsing unsubscribes. Both frame types land in the
 * store's single reducer, so nothing here writes member state directly.
 *
 * Per TONIC-PEX-IMPL-FE-001 Task 6.
 *
 * @module features/executionStudio/hooks/useBatchRun
 */

/** How often to re-read members once the stream is declared degraded. */
const DEGRADED_POLL_MS = 10_000;

/**
 * Per-run reads go out in small waves rather than all at once — ten members is
 * ten requests, and firing forty in parallel is how you get throttled.
 */
const READ_CONCURRENCY = 4;

/**
 * A single-run read speaks run vocabulary (IN_PROGRESS, COMPLETED, a `result` of
 * PASSED/FAILED); the tracker speaks member vocabulary (RUNNING, PASSED, PARTIAL,
 * FAILED…). Without this, a script that finished came back as COMPLETED — not a
 * member status, never terminal — and a restored batch sat on "running" forever.
 *
 * A finished run is sorted by its counts: nothing failed → PASSED, some of each
 * → PARTIAL, nothing passed → FAILED. A cancel always reads as CANCELLED.
 */
export const toMemberStatus = (run, outcome, counts) => {
  if (outcome?.isCancelled) return MEMBER_STATUS.CANCELLED;
  const raw = String(run?.status ?? run?.testResults?.testSummary?.runStatus ?? '').toUpperCase();
  if (Object.prototype.hasOwnProperty.call(MEMBER_STATUS, raw) && raw !== 'PENDING') return raw;
  if (raw === 'PENDING' || raw === 'SUBMITTED') return MEMBER_STATUS.QUEUED;
  if (raw === 'IN_PROGRESS' || raw === 'STARTED' || raw === 'EXECUTING') return MEMBER_STATUS.RUNNING;
  if (raw === 'ERROR') return MEMBER_STATUS.FAILED;

  const result = String(run?.result ?? '').toUpperCase();
  const finished = raw === 'COMPLETED' || raw === 'DONE' || raw === 'FINISHED'
    || result === 'PASSED' || result === 'FAILED' || result === 'ERROR';
  if (!finished) return raw || undefined;

  const passed = counts?.passed ?? 0;
  const failed = counts?.failed ?? 0;
  if (failed === 0 && (passed > 0 || result === 'PASSED')) return MEMBER_STATUS.PASSED;
  if (failed > 0 && passed > 0) return MEMBER_STATUS.PARTIAL;
  return MEMBER_STATUS.FAILED;
};

export const useBatchRun = (userData, { groupingId = null, onBatchComplete = null } = {}) => {
  const { startBatch, applyFrame, markCancelling, markSubmitFailed, setExpanded, clearSequence, setDetailLoading, setStreamDegraded, reset } =
    useBatchRunActions();
  const pollTimerRef = useRef(null);
  const quietReadRef = useRef(false);
  const completedBatchRef = useRef(null);

  /** The authoritative verdict, once the Core has written it. */
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');

  // The consolidated report, fetched once the batch is terminal so it appears
  // with no manual refresh (Task 7).


  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  /**
   * Rebuild member state from **per-run reads**.
   *
   * `GET /batches/{id}` was deleted, so there is no single call that returns the
   * batch. Each member is read with the endpoint the app already uses for a
   * single run, and the results fold through the same reducer as socket frames —
   * still no second write path for member state.
   *
   * Reads are unstamped (no `sequence`), so they always apply without disturbing
   * the frame watermark, which is what a rehydrate should do.
   */
  const readMembers = useCallback(async () => {
    const { projectId, members, order } = useBatchRunStore.getState();
    if (!projectId) return;

    // OUT_OF_SCOPE members never ran and have no runId
    const targets = order
      .map((runId) => members[runId])
      .filter((m) => m?.runId && m?.runnableId);

    const { TestService } = await import('../../../shared/middleware/services/TestService');

    for (let i = 0; i < targets.length; i += READ_CONCURRENCY) {
      const wave = targets.slice(i, i + READ_CONCURRENCY);
      // eslint-disable-next-line no-await-in-loop
      const results = await Promise.allSettled(
        // Uncached: a cached "still running" is exactly what must not be shown
        // after a refresh or while polling
        wave.map((m) => TestService.getRun(projectId, m.runnableId, m.runId, userData, null, { bypassCache: true }))
      );
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled' || !result.value) return;
        const run = result.value;
        const summary = run.testResults?.testSummary;
        // A cancelled member still reports runStatus: COMPLETED — the engine
        // finished tidying up, which is not the test finishing.
        const outcome = resolveRunOutcome(summary);
        const permutations = summary ? {
          total: summary.numberOfPermutations ?? null,
          executed: summary.numberOfPermutationsExecuted ?? 0,
          passed: summary.numberOfPermutationsPassed ?? 0,
          failed: summary.numberOfPermutationsFailed ?? 0,
        } : undefined;
        applyFrame({
          runid: wave[index].runId,
          status: toMemberStatus(run, outcome, permutations),
          failureReason: outcome.cancelReason ?? undefined,
          permutations,
          startedAt: run.startedAt,
          endedAt: run.endedAt,
          // A read, not a live sighting — must not stamp "now" as a start/end
          observed: false,
        });
      });
    }
  }, [userData, applyFrame]);

  /**
   * Re-read members while the stream is degraded, so silence never looks idle.
   * A fallback only — the WebSocket stays the primary path, and this stops as
   * soon as every member is terminal.
   */
  const startPolling = useCallback((batchId) => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(async () => {
      try {
        await readMembers();
        const { order, terminal } = useBatchRunStore.getState();
        if (order.length > 0 && order.every((runId) => terminal[runId])) stopPolling();
      } catch (err) {
        logger.warn('Degraded-mode member read failed', { error: err.message, batchId });
      }
    }, DEGRADED_POLL_MS);
  }, [readMembers, stopPolling]);

  /**
   * Fetch the consolidated summary the Core writes when the last member ends.
   *
   * By the time the batch-terminal frame arrives the file already exists, so
   * one attempt is normally enough. When completion is inferred from the rows
   * instead — the terminal frame can be lost like any other — a 404 means "not
   * written yet", so it is retried with backoff rather than reported as broken.
   */
  const fetchReport = useCallback(async ({ retry = false } = {}) => {
    const { projectId, projectName, batchRunNumber, batchId, groupingId: batchGroupingId } = useBatchRunStore.getState();
    // The running batch knows its own grouping; the option is the fallback
    const reportGroupingId = batchGroupingId || groupingId;
    if (!projectId || !projectName || batchRunNumber == null || !reportGroupingId) return null;
    // A retry can outlive its run. Once another batch has started (or the run
    // was closed), this one's result must not land on the new run.
    const stillCurrent = () => useBatchRunStore.getState().batchId === batchId;

    setReportLoading(true);
    setReportError('');
    const delays = retry ? [0, 1500, 4000, 8000] : [0];

    try {
      const { TestService } = await import('../../../shared/middleware/services/TestService');
      for (let i = 0; i < delays.length; i += 1) {
        if (delays[i] > 0) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, delays[i]));
          if (!stillCurrent()) return null;
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          const summary = await TestService.getConsolidatedSummary(
            projectId, projectName, reportGroupingId, batchRunNumber, userData
          );
          if (!stillCurrent()) return null;
          setReport(summary);
          return summary;
        } catch (err) {
          const notYet = err?.response?.status === 404;
          if (!stillCurrent()) return null;
          if (!notYet || i === delays.length - 1) {
            logger.warn('Consolidated summary not available', {
              error: err.message, batchRunNumber, groupingId: reportGroupingId,
            });
            setReportError(notYet
              ? 'The consolidated report has not been written yet.'
              : 'Could not load the consolidated report.');
            return null;
          }
        }
      }
      return null;
    } finally {
      if (stillCurrent()) setReportLoading(false);
    }
  }, [groupingId, userData]);

  /**
   * Tell the caller once every member has finished, with the grouping's rolled-up
   * outcome, so the grouping file can be stamped with its terminal status.
   */
  useEffect(() => useBatchRunStore.subscribe((state) => {
    const { batchId, order, terminal } = state;
    if (!batchId || order.length === 0) return;
    if (completedBatchRef.current === batchId) return;

    const streaming = order.filter((runId) => state.members[runId]?.runId);
    if (streaming.length === 0 || !streaming.every((runId) => terminal[runId])) return;

    completedBatchRef.current = batchId;
    // Nothing more can report, so the watchdog must stop treating silence as a
    // broken connection.
    runStreamService.setBatchLiveness(false);
    stopPolling();
    if (onBatchComplete) onBatchComplete({ batchId, ...selectGroupingOutcome(state) });

    // The terminal frame means the files are already in S3; inferring
    // completion from the rows means they may be a moment behind, so retry.
    fetchReport({ retry: !state.batchStatus });
  }), [onBatchComplete, fetchReport, stopPolling]);

  /** Wire the stream service's observers once. */
  useEffect(() => {
    runStreamService.setBatchObservers({
      onFrame: (frame) => applyFrame(frame),
      // Raised only once the socket is gone and reconnects are exhausted
      onDegraded: (batchId) => {
        setStreamDegraded(true);
        if (batchId) startPolling(batchId);
      },
      // The socket is up but nothing has changed for a while — normal for a
      // long script. Refresh the rows without telling the user anything broke.
      onQuiet: (batchId) => {
        if (quietReadRef.current) return;
        quietReadRef.current = true;
        readMembers()
          .catch((err) => logger.warn('Quiet-stream member read failed', { error: err.message, batchId }))
          .finally(() => { quietReadRef.current = false; });
      },
      // A heartbeat proves the stream is healthy — drop the banner and the
      // fallback polling it started
      onAlive: () => {
        if (useBatchRunStore.getState().streamDegraded) {
          setStreamDegraded(false);
          stopPolling();
        }
      },
    });
    return () => {
      runStreamService.setBatchObservers({ onFrame: null, onDegraded: null, onQuiet: null, onAlive: null });
      stopPolling();
    };
  }, [applyFrame, readMembers, setStreamDegraded, startPolling, stopPolling]);

  /**
   * Submit a grouping and start streaming.
   *
   * The 202 carries every member's runId and paths before anything runs, so the
   * rows and the stream.json backfill map are both known at submit time.
   */
  const submit = useCallback(async ({ projectId, projectName, runnableGroupPath, groupingName, groupingId: runGroupingId }) => {
    // One GTID for the whole batch — the Middleware mints the per-member children
    const gtid = BatchService.newTransaction();
    const batch = await BatchService.create(
      projectId,
      { runnableGroupPath, projectName, gtid },
      userData
    );

    startBatch({
      batchId: batch.batchId,
      // Together these locate every artefact the batch writes:
      // …/groupings/{groupingId}/runs/run{batchRunNumber}/
      batchRunNumber: batch.batchRunNumber,
      // Passed per run by a caller that serves every grouping (the app-wide
      // run window); the hook option covers a caller bound to one grouping
      groupingId: runGroupingId || groupingId,
      gtid,
      groupingName,
      projectId,
      projectName,
      timeZone: batch.timeZone,
      members: (batch.members || []).map((m) => ({
        runId: m.runId,
        // The 202 is the only response carrying runId and runnableId together
        runnableId: m.runnableId,
        name: m.displayName,
        scriptTypes: m.scriptTypes || [],
        status: m.status,
        // Without this a SUBMIT_FAILED row says only "could not be dispatched"
        failureReason: m.failureReason,
        logPath: m.logPath,
        reportPath: m.reportPath,
        summaryPath: m.summaryPath,
      })),
    });

    // Nothing dispatched — there is no stream to open and nothing to poll for.
    // Saying "live updates are degraded" here would blame the connection for a
    // batch the Router rejected outright.
    const { order, terminal } = useBatchRunStore.getState();
    if (order.length > 0 && order.every((runId) => terminal[runId])) {
      logger.warn('Batch finished at submit — no member was dispatched', {
        batchId: batch.batchId,
      });
      return batch;
    }

    if (batch.webSocketUrl) {
      // No member is expanded yet, so the subscription set is empty. Without
      // this the degraded watchdog would read the batch as already finished.
      runStreamService.setBatchLiveness(true);
      runStreamService.connectBatch({
        batchId: batch.batchId,
        webSocketUrl: batch.webSocketUrl,
        runIds: [], // members are subscribed individually, only while expanded
      });
    } else {
      // Rows would render and then never move. Say so and poll instead of
      // leaving the user watching a frozen tracker.
      logger.error('Batch response carried no webSocketUrl — falling back to polling', {
        batchId: batch.batchId,
      });
      setStreamDegraded(true);
      startPolling(batch.batchId);
    }

    return batch;
  }, [userData, startBatch, setStreamDegraded, startPolling, groupingId]);

  /**
   * Rehydrate after a page refresh.
   *
   * There is no batch read, so the caller supplies what the create-202 gave it
   * (batchId, members with runId + runnableId) and state is rebuilt from
   * per-run reads.
   */
  const rehydrate = useCallback(async ({
    batchId, batchRunNumber, groupingName, projectId, projectName, timeZone, members, webSocketUrl,
    groupingId: runGroupingId, startedAt,
  }) => {
    startBatch({
      batchId, batchRunNumber, groupingId: runGroupingId || groupingId, groupingName,
      projectId, projectName, timeZone, members, startedAt,
    });
    await readMembers();

    const { order, terminal } = useBatchRunStore.getState();
    const finished = order.length > 0 && order.every((runId) => terminal[runId]);
    if (finished) return;

    if (webSocketUrl) {
      runStreamService.setBatchLiveness(true);
      runStreamService.connectBatch({ batchId, webSocketUrl, runIds: [] });
    } else {
      logger.error('Rehydrate had no webSocketUrl — falling back to member reads', { batchId });
      setStreamDegraded(true);
      startPolling(batchId);
    }
  }, [startBatch, readMembers, setStreamDegraded, startPolling, groupingId]);

  /**
   * Backfill an expanded member's history from its `stream.json`, so the panel
   * shows the steps that already happened rather than only what arrives next.
   *
   * Reuses `TestService.getRunSnapshot` — the same fetch the single-run path
   * uses — rather than adding a second way to read a snapshot. The result is
   * unstamped, so it applies without disturbing the sequence watermark.
   */
  const hydrateDetail = useCallback(async (runId) => {
    const { members, projectId, projectName } = useBatchRunStore.getState();
    const row = members[runId];
    // `logPath` is the 202's when it sent one and the grouping's runs folder
    // otherwise — `startBatch` settles that, so there is nothing to decide here.
    if (!row?.logPath || !projectName || !projectId) return;

    setDetailLoading(runId, true);
    try {
      const { TestService } = await import('../../../shared/middleware/services/TestService');
      const snapshot = await TestService.getRunSnapshot(
        projectId, row.logPath, projectName, runId, userData
      );
      if (snapshot?.testResults) {
        applyFrame({ runid: runId, testResults: snapshot.testResults });
      }
    } catch (err) {
      // 404 simply means the snapshot is not written yet — the stream fills in
      logger.debug('Member snapshot not available yet', { runId, error: err.message });
    } finally {
      setDetailLoading(runId, false);
    }
  }, [userData, applyFrame, setDetailLoading]);

  /**
   * Expand one member: subscribe to its full stream and backfill its history.
   * Only one at a time, so the previously expanded member is unsubscribed
   * first — that is what keeps collapsed-view traffic to the slim frames.
   */
  const expand = useCallback((runId) => {
    const previous = setExpanded(runId);
    if (previous && previous !== runId) {
      runStreamService.unsubscribeRun(previous);
      clearSequence(previous);
    }
    if (runId) {
      // Each member has its own runnableId — the backend rejects a subscribe
      // that arrives without it and the panel would never fill.
      const { projectId, members } = useBatchRunStore.getState();
      runStreamService.subscribeRun(runId, {
        projectId,
        runnableId: members[runId]?.runnableId,
      });
      hydrateDetail(runId);
    }
  }, [setExpanded, clearSequence, hydrateDetail]);

  const collapse = useCallback(() => {
    const previous = setExpanded(null);
    if (previous) {
      runStreamService.unsubscribeRun(previous);
      clearSequence(previous);
    }
  }, [setExpanded, clearSequence]);

  /**
   * Cancel the whole batch. The 202 says how many were actually affected and
   * how many had already finished — report both, never "all stopped".
   */
  const cancelBatch = useCallback(async () => {
    const { batchId, members, terminal } = useBatchRunStore.getState();
    const stoppable = Object.values(members)
      .filter((m) => m.runId && !terminal[m.runId])
      .map((m) => m.runId);

    // Mark only once the request is accepted. Marking first meant a failed
    // cancel left every row showing CANCELLING while nothing had been cancelled.
    const result = await BatchService.cancelBatch(batchId, userData);
    markCancelling(stoppable);
    return result;
  }, [userData, markCancelling]);

  /** Cancel one script — a batch member or a standalone run. */
  const cancelRun = useCallback(async (runId) => {
    const result = await BatchService.cancelRun(runId, userData);
    markCancelling([runId]);
    return result;
  }, [userData, markCancelling]);

  const teardown = useCallback(() => {
    stopPolling();
    runStreamService.setBatchLiveness(false);
    runStreamService.disconnect();
    completedBatchRef.current = null;
    setReport(null);
    setReportError('');
    setReportLoading(false);
    reset();
  }, [stopPolling, reset]);

  return {
    report,
    reportLoading,
    reportError,
    fetchReport,
    submit,
    rehydrate,
    expand,
    collapse,
    hydrateDetail,
    cancelBatch,
    cancelRun,
    markSubmitFailed,
    teardown,
  };
};

export default useBatchRun;
